import React, { useState, cloneElement } from 'react';
import { SectionErrorBoundary } from '../components/SectionErrorBoundary';
import { FileText, ShoppingBag, CreditCard, Heart, MessageSquare, Settings, LogOut, Download, CalendarDays, Sparkles, Star, AlertCircle, Package } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { useUser } from '../contexts/UserContext';
import { zodiacThemes, getZodiacSign } from '../components/user-dashboard/zodiacThemes';
import { OverviewTab } from '../components/user-dashboard/OverviewTab';
import { ProfileTab } from '../components/user-dashboard/ProfileTab';
import { CalendarTab } from '../components/user-dashboard/CalendarTab';
import { ReportsTab } from '../components/user-dashboard/ReportsTab';
import { OrdersTab } from '../components/user-dashboard/OrdersTab';
import { CreditsTab } from '../components/user-dashboard/CreditsTab';
import { SubscriptionsTab } from '../components/user-dashboard/SubscriptionsTab';
import { FavoritesTab } from '../components/user-dashboard/FavoritesTab';
import { ChatTab } from '../components/user-dashboard/ChatTab';
import { SettingsTab } from '../components/user-dashboard/SettingsTab';
import { PurchasesTab } from '../components/user-dashboard/PurchasesTab';
interface UserDashboardPageProps {
  onNavigate: (page: string) => void;
}
export function UserDashboardPage({
  onNavigate
}: UserDashboardPageProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const {
    user,
    logout
  } = useUser();
  // User Astro Data State
  const [birthData, setBirthData] = useState({
    date: '1990-04-12',
    time: '14:30',
    place: 'Москва, Россия'
  });
  const currentSign = getZodiacSign(birthData.date);
  const theme = zodiacThemes[currentSign];
  const menuItems = [{
    id: 'overview',
    label: 'Обзор',
    icon: <FileText className="w-5 h-5" data-id="element-22506" />
  }, {
    id: 'profile',
    label: 'Астро-профиль',
    icon: <Sparkles className="w-5 h-5" data-id="element-22507" />
  }, {
    id: 'calendar',
    label: 'Мой календарь',
    icon: <CalendarDays className="w-5 h-5" data-id="element-22508" />
  }, {
    id: 'reports',
    label: 'Мои отчёты',
    icon: <Download className="w-5 h-5" data-id="element-22509" />
  }, {
    id: 'orders',
    label: 'Заказы',
    icon: <ShoppingBag className="w-5 h-5" data-id="element-22510" />
  }, {
    id: 'purchases',
    label: 'Мои покупки',
    icon: <Package className="w-5 h-5" data-id="element-22511" />
  }, {
    id: 'credits',
    label: 'Кредиты',
    icon: <CreditCard className="w-5 h-5" data-id="element-22512" />
  }, {
    id: 'subscriptions',
    label: 'Подписки',
    icon: <Star className="w-5 h-5" data-id="element-22513" />
  }, {
    id: 'favorites',
    label: 'Избранное',
    icon: <Heart className="w-5 h-5" data-id="element-22514" />
  }, {
    id: 'chat',
    label: 'Сообщения',
    icon: <MessageSquare className="w-5 h-5" data-id="element-22515" />
  }, {
    id: 'settings',
    label: 'Настройки',
    icon: <Settings className="w-5 h-5" data-id="element-22516" />
  }];
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab theme={theme} user={user} setActiveTab={setActiveTab} data-id="element-22517" />;
      case 'profile':
        return <ProfileTab theme={theme} birthData={birthData} setBirthData={setBirthData} setActiveTab={setActiveTab} data-id="element-22518" />;
      case 'calendar':
        return <CalendarTab theme={theme} data-id="element-22519" />;
      case 'reports':
        return <ReportsTab theme={theme} onNavigate={onNavigate} data-id="element-22520" />;
      case 'orders':
        return <OrdersTab theme={theme} onNavigate={onNavigate} setActiveTab={setActiveTab} data-id="element-22521" />;
      case 'purchases':
        return <PurchasesTab onNavigate={onNavigate} data-id="element-22522" />;
      case 'credits':
        return <CreditsTab theme={theme} data-id="element-22523" />;
      case 'subscriptions':
        return <SubscriptionsTab theme={theme} onNavigate={onNavigate} data-id="element-22524" />;
      case 'favorites':
        return <FavoritesTab theme={theme} onNavigate={onNavigate} data-id="element-22525" />;
      case 'chat':
        return <ChatTab theme={theme} data-id="element-22526" />;
      case 'settings':
        return <SettingsTab theme={theme} user={user} data-id="element-22527" />;
      default:
        return <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-id="element-22528">
            <AlertCircle className="w-12 h-12 mb-4 text-slate-300" data-id="element-22529" />
            <p data-id="element-22530">Раздел в разработке</p>
          </div>;
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24 font-sans" data-id="element-22531">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-id="element-22532">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-22533">
          {/* Sidebar */}
          <div className="lg:col-span-3" data-id="element-22534">
            {/* Mobile Tabs */}
            <div className="lg:hidden mb-6" data-id="element-22535">
              <div className={`flex items-center gap-3 mb-4 p-4 rounded-2xl bg-gradient-to-r ${theme.lightGradient} border ${theme.border}`} data-id="element-22536">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white font-bold text-lg shadow-sm`} data-id="element-22537">
                  {theme.icon}
                </div>
                <div data-id="element-22538">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-22539">
                    {user?.name || 'Пользователь'}
                  </div>
                  <div className={`text-xs font-medium ${theme.text}`} data-id="element-22540">
                    {theme.name}
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-22541">
                {menuItems.map(item => <button key={item.id} onClick={() => setActiveTab(item.id)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeTab === item.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'bg-white border border-slate-200 text-slate-600'}`} data-id="element-22542">
                    {cloneElement(item.icon as React.ReactElement, {
                  className: 'w-3.5 h-3.5'
                })}
                    {item.label}
                  </button>)}
              </div>
            </div>

            {/* Desktop Sidebar */}
            <Card padding="none" className="sticky top-24 overflow-hidden hidden lg:block shadow-sm" data-id="element-22543">
              <div className={`p-6 border-b ${theme.border} bg-gradient-to-b ${theme.lightGradient}`} data-id="element-22544">
                <div className="flex flex-col items-center text-center" data-id="element-22545">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-3xl text-white shadow-md mb-3 relative`} data-id="element-22546">
                    {theme.icon}
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold text-slate-700 shadow-sm uppercase" data-id="element-22547">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </div>
                  <div className="font-bold text-slate-900 text-lg" data-id="element-22548">
                    {user?.name || 'Пользователь'}
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-wider mt-1 ${theme.text}`} data-id="element-22549">
                    {theme.name}
                  </div>
                </div>
              </div>
              <nav className="p-3 space-y-1" data-id="element-22550">
                {menuItems.map(item => <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all ${activeTab === item.id ? `${theme.bg} ${theme.text} shadow-sm` : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-22551">
                    <span className={`mr-3 ${activeTab === item.id ? theme.text : 'text-slate-400'}`} data-id="element-22552">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>)}
                <div className="pt-4 mt-4 border-t border-slate-100" data-id="element-22553">
                  <button onClick={() => {
                  logout();
                  onNavigate('home');
                }} className="w-full flex items-center px-4 py-3 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-22554">
                    <LogOut className="w-5 h-5 mr-3 text-rose-400" data-id="element-22555" /> Выйти
                  </button>
                </div>
              </nav>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-9" data-id="element-22556">
            <SectionErrorBoundary key={activeTab} sectionName={`dashboard-${activeTab}`} fallbackMinHeight="min-h-[400px]" data-id="element-22557">
              {renderContent()}
            </SectionErrorBoundary>
          </div>
        </div>
      </div>
    </div>;
}